import { AppState } from 'app-store'
import { get } from 'lodash-es'
import { buyCheckoutActions } from '@buy/state/checkout/actions'
import { BuySelectedAddon, BuyAddon } from '@buy/state/add-on'
import { getFatalError, getAccountState, getLocationState } from '@common/state/selectors'
import {
    getUserState,
    getLabelContent,
    getCheckoutState,
    getPlanData,
    getBuyFormState,
    getCouponState,
} from '@buy/state/selectors'
import { getExtraAddons, getCurrentAgent, getCurrentEndpoint } from '@buy/utils'
import { getUpgradingStatus } from '@buy/utils/upgrade-user'
import { BuyFormData } from '@buy/state/buy-form/state'
import { OrderSummaryLine } from '@buy/state/order-summary/order-summary'
import { BuyCustomizeState } from './state'

export const mapBuyCustomizeState = (state: AppState): BuyCustomizeState => {
    const {
        selectedPlan,
        changePlan,
        upgradeClose,
        quantityLabelLong,
        quantityLabelPlural,
        quantityLabel,
        continue: contentContinue,
        quantityPopupInfo,
        additionalUserLicences,
        howManyMore,
        existingUserLicencePlural,
        existingUserLicense,
        additional,
        changePlanMethodModalHeader,
        changePlanModalSubheader,
        modalCloseButtonText,
        edit,
        changeNumberModalHeader,
        changeNumberModalContent,
        changePlanUrl,
        couponDisclaimer,
        monthly,
        annual,
        user,
        year,
        month,
        billedMonthlyPrice,
        billedAnnualPrice,
        selectAddons,
        selectAddonsDescription,
        per,
        organizer,
        taxTooltip,
        agentsVolumediscountTooltip,
        endpointsVolumediscountTooltip,
        mobileVolumediscountTooltip,
        cameraShareVolumediscountTooltip,
        endpointProtectionVolumediscountTooltip,
        proratedadjustmentTooltip,
    } = getLabelContent(state)

    const {
        organizers,
        selectedAddons,
        billingFrequency,
        selectedTierIndex,
        endpoints,
        extraAddons: extraAddonsSelected,
        agents,
        calculatedTotal,
        totalLoading,
        baseLine,
        additionalLines,
        addonLines,
        billingTenure,
    } = getCheckoutState(state)

    const { accountSubscriptions } = getAccountState(state)

    const { initialData, statesOptional } = getBuyFormState(state)
    const planData = getPlanData(state)
    const isUserUpgrading = getUpgradingStatus(accountSubscriptions)
    const coupon = getCouponState(state)
    const { isCouponValidAndApplied, urlCouponCode, couponServerData } = coupon
    const {
        userType,
        existingAccountInformation,
        loggedIn: { isUserLoggedIn },
    } = getUserState(state)
    const { fatalErrorBlocker } = getFatalError(state)
    const currentAgent = getCurrentAgent(planData.addons)
    const currentEndpoint = getCurrentEndpoint(planData.addons)
    const { locationInfo } = getLocationState(state)
    const { locale: Locale, currency: CurrencyCode } = locationInfo
    const baseTierMonthlyPrice: number = get(
        planData,
        ['pricingInfo', CurrencyCode, 'monthly', `tier${selectedTierIndex + 1}`, 'price'],
        0
    )
    const baseTierAnnualPrice: number = get(
        planData,
        ['pricingInfo', CurrencyCode, 'annual', `tier${selectedTierIndex + 1}`, 'price'],
        0
    )
    const extraAddons = getExtraAddons(planData.addons).reverse()
    const billingPeriodKeys = planData.billingPeriods.map((item) => item.key)

    return {
        content: {
            selectedPlan,
            changePlan,
            upgradeClose,
            quantityLabelLong,
            quantityLabelPlural,
            quantityLabel,
            continue: contentContinue,
            quantityPopupInfo,
            additionalUserLicences,
            howManyMore,
            existingUserLicencePlural,
            existingUserLicense,
            additional,
            changePlanMethodModalHeader,
            changePlanModalSubheader,
            modalCloseButtonText,
            edit,
            changeNumberModalHeader,
            changeNumberModalContent,
            changePlanUrl,
            couponDisclaimer,
            monthly,
            annual,
            user,
            year,
            month,
            billedMonthlyPrice,
            billedAnnualPrice,
            selectAddons,
            selectAddonsDescription,
            per,
            organizer,
            taxTooltip,
            agentsVolumediscountTooltip,
            endpointsVolumediscountTooltip,
            mobileVolumediscountTooltip,
            cameraShareVolumediscountTooltip,
            endpointProtectionVolumediscountTooltip,
            proratedadjustmentTooltip,
        },
        organizers,
        planData,
        selectedPlan: planData,
        isUserLoggedIn,
        selectedAddons,
        userType,
        fatalErrorBlocker,
        existingAccountInformation,
        initialData,
        billingFrequency,
        isCouponValidAndApplied,
        urlCouponCode,
        couponServerData,
        locale: Locale,
        selectedTierIndex,
        CurrencyCode,
        coupon,
        currentAgent,
        currentEndpoint,
        endpoints,
        baseTierMonthlyPrice,
        baseTierAnnualPrice,
        extraAddons,
        extraAddonsSelected,
        agents,
        accountSubscriptions,
        calculatedTotal,
        totalLoading,
        statesOptional,
        isUserUpgrading,
        baseLine,
        additionalLines,
        addonLines,
        billingPeriodKeys,
        billingTenure,
    }
}
export const mapBuyCustomizeActions = {
    setOrganizers: (organizer: number) => buyCheckoutActions.setOrganizers(organizer),
    setSelectedAddons: (addons: BuySelectedAddon[]) => buyCheckoutActions.setSelectedAddons(addons),
    calculatePrice: (values: BuyFormData, planSKU?: string) =>
        buyCheckoutActions.calculatePrice(values, 'customize', planSKU),
    setExtraAddons: (addons: BuyAddon[]) => buyCheckoutActions.setExtraAddons(addons),
    setEndpoints: (endpoints: number) => buyCheckoutActions.setEndpoints(endpoints),
    setAgents: (agents: number) => buyCheckoutActions.setAgents(agents),
    setBaseLine: (baseLine: OrderSummaryLine) => buyCheckoutActions.setBaseLine(baseLine),
    setAdditionalLines: (additionalLines: Array<OrderSummaryLine>) =>
        buyCheckoutActions.setAdditionalLines(additionalLines),
    setAddonLines: (addonLines: Array<OrderSummaryLine>) => buyCheckoutActions.setAddonLines(addonLines),
}

export type BuyCustomizeActionsType = typeof mapBuyCustomizeActions
